// ─── Clear This Season's Stats (Settings → Advanced) ─────────────────────────
//
// Starts the CURRENT season's stats over, so a coach can re-import cleanly.
// The button used to zero only each player's season line and pitching log. The
// Stats tab sums the game docs, so it didn't change at all: the coach thought
// nothing happened while ratings and pitcher rest were quietly degraded
// (support log G1, ticket #1).
//
// Erased, current season only:
//   - every game's imported stats, GameChanger field time, pitch counts, import
//     record, and play-by-play / insight-card subcollections
//   - the hidden "Season stats import" signup game and the typed starting record
//   - each player's season line, season CSV snapshot and pitching log (so
//     pitcher rest starts over too: Dan's call, the confirm warns about it),
//     and the bench counts that came from GameChanger field time
// Kept: roster and ratings, schedule, scores and results, Dugout's inning-by-
// inning positions and the bench counts they gave, game balls, catcher logs,
// past seasons. A game being played live is skipped entirely.
//
// planClearSeasonStats is pure (scripts/test-clear-season-stats.ts covers it);
// clearSeasonStats carries a plan out.

import { collection, deleteField, doc, getDocs, writeBatch, type Firestore, type WriteBatch } from 'firebase/firestore';
import { emptyStats } from './season-rollover';
import { benchInningsByPlayer } from './game-positions';
import { currentSeasonGames } from './utils';
import { isGameLive } from './game-utils';

/** Game-doc fields an import (or the live engine's pitch counts) writes. */
export const CLEARED_GAME_FIELDS = ['stats', 'gcFieldTime', 'inferredInnings', 'pitchCounts', 'gcImport', 'importedFrom'] as const;

/** Per-game subcollections written by the GameChanger import pipeline. */
const CLEARED_GAME_SUBCOLLECTIONS = ['atBats', 'insights'] as const;

export interface ClearSeasonPlan {
  /** Games holding any of CLEARED_GAME_FIELDS, and which ones to delete. */
  games: { id: string; fields: string[] }[];
  /** Every game cleared, for its play-by-play and insight cards (read at clear time). */
  subcollectionGameIds: string[];
  /** Games with imported player stats, for the confirm's count. */
  statsGameCount: number;
  /** The hidden signup import game, when it belongs to this season. */
  signupGameId: string | null;
  clearStartRecord: boolean;
  players: { id: string; patch: Record<string, any> }[];
  /** Games left alone because they're being played right now. */
  skippedLiveGameIds: string[];
}

export function planClearSeasonStats({ team, players, seasonGames, signupGame }: {
  team: any;
  players: any[];
  /** Current-season games (the Dashboard's seasonGames: no practice, no signup game). */
  seasonGames: any[];
  /** The Dashboard's onboardingStatsGame, if any. */
  signupGame?: any | null;
}): ClearSeasonPlan {
  const games = seasonGames.filter(g => g?.id && !isGameLive(g));
  const skippedLiveGameIds = seasonGames.filter(g => g?.id && isGameLive(g)).map(g => g.id);

  const gamePatches = games
    .map(g => ({ id: g.id, fields: CLEARED_GAME_FIELDS.filter(f => g[f] !== undefined) as string[] }))
    .filter(p => p.fields.length > 0);

  // Bench counts from GameChanger: once a game's GC field time is gone, its
  // entry becomes what Dugout's own positions say (as End Game counts them), or
  // goes if Dugout has none for that player. Entries End Game wrote for games
  // without GC field time already came from Dugout and are left alone.
  const gcGames = games.filter(g => g.gcFieldTime && Object.keys(g.gcFieldTime).length > 0);
  const playerPatches = players.filter(p => p?.id).map(p => {
    const benchLog: Record<string, number> = { ...(p.benchLog && typeof p.benchLog === 'object' ? p.benchLog : {}) };
    for (const g of gcGames) {
      const fromDugout = g.inningLogs && Object.keys(g.inningLogs).length > 0 ? benchInningsByPlayer(g.inningLogs) : {};
      if (p.id in fromDugout) benchLog[g.id] = fromDugout[p.id];
      else delete benchLog[g.id];
    }
    return {
      id: p.id,
      patch: {
        stats: emptyStats(),
        // null, not deleted: the same reset Start New Season writes.
        seasonSnapshot: null,
        pitchingLog: [],
        benchLog,
        seasonBenchInnings: Object.values(benchLog).reduce((sum, n) => sum + (Number(n) || 0), 0),
      },
    };
  });

  const signupInSeason = !!signupGame?.id && currentSeasonGames([signupGame], team).length > 0;

  return {
    games: gamePatches,
    subcollectionGameIds: games.map(g => g.id),
    statsGameCount: gamePatches.filter(p => p.fields.includes('stats')).length,
    signupGameId: signupInSeason ? signupGame.id : null,
    clearStartRecord: !!team?.seasonStartRecord,
    players: playerPatches,
    skippedLiveGameIds,
  };
}

/**
 * Carry out a plan. Chunked at 450 writes per batch (Firestore's limit is 500),
 * so it is not one atomic write; if it stops partway, running it again finishes
 * the job. update(), not set(merge): benchLog must be REPLACED so removed
 * entries actually go.
 */
export async function clearSeasonStats(db: Firestore, teamId: string, plan: ClearSeasonPlan): Promise<void> {
  const ops: ((b: WriteBatch) => void)[] = [];

  // Subcollections aren't removed with their game's fields; delete their docs.
  const snaps = await Promise.all(plan.subcollectionGameIds.flatMap(gameId =>
    CLEARED_GAME_SUBCOLLECTIONS.map(sub => getDocs(collection(db, `teams/${teamId}/games/${gameId}/${sub}`)))));
  snaps.forEach(snap => snap.docs.forEach(d => ops.push(b => b.delete(d.ref))));

  const now = new Date().toISOString();
  for (const { id, fields } of plan.games) {
    const clear = Object.fromEntries(fields.map(f => [f, deleteField()]));
    ops.push(b => b.update(doc(db, `teams/${teamId}/games`, id), { ...clear, updatedAt: now }));
  }
  if (plan.signupGameId) {
    const signupGameId = plan.signupGameId;
    ops.push(b => b.delete(doc(db, `teams/${teamId}/games`, signupGameId)));
  }
  for (const { id, patch } of plan.players) {
    ops.push(b => b.update(doc(db, `teams/${teamId}/players`, id), { ...patch, updatedAt: now }));
  }
  if (plan.clearStartRecord) {
    ops.push(b => b.update(doc(db, 'teams', teamId), { seasonStartRecord: deleteField(), updatedAt: now }));
  }

  const CHUNK = 450;
  for (let i = 0; i < ops.length; i += CHUNK) {
    const batch = writeBatch(db);
    ops.slice(i, i + CHUNK).forEach(op => op(batch));
    await batch.commit();
  }
}
